/*
 * 万圣节转盘落地页（sections/halloween-landing.liquid）的 GA4 埋点，事件契约见 openspec change nexbie-us-halloween-ga-tracking。
 * 只发 GA4，事件统一 hw_ 前缀；不发邮箱、码、ref 等任何个人信息。活动结束后与页面一起删除。
 *
 *   hw_section_view        区块露出自身一半或占满半屏并持续 1s，每区块一次      section_name
 *   hw_scroll_depth        25 / 50 / 75 / 90 / 100%，各一次                  percent_scrolled
 *   hw_cta_click           首屏 CTA / 倒计时条 Shop / View more（展开时）      cta_name
 *   hw_spin_click          抽奖表单提交                                     button_location（form / hub / keyboard）
 *   hw_product_click       选鞋卡 Add / 图片或名称去商详 / Size Chart          action / product_handle
 *   hw_share action=app    分享弹窗里的应用                                  app
 *   hw_ugc_click           晒图清单账号链接 / i 提示（展开时）                  action / channel
 * 下面这些只有业务脚本 nx-halloween.js 知道结果，由它在 document 上派发 CustomEvent('nx-hw:track', { detail: { name, params } })，
 * 这里收下入队：hw_spin_result、hw_card_action、hw_share（open / copy）、hw_referral_landing、hw_add_to_cart_click / success / fail。
 * 业务脚本不调用 gtag；本文件没加载或出错时那些事件无人接收，页面照常。
 * 本文件必须在 nx-halloween.js 之前执行（section 里排在它前面，都是 defer）：好友到达事件在业务脚本初始化时就派发。
 *
 * 发送层复制自 assets/lp-analytics.js（WEI-33，2026-10-12 版）的 send / pump / flushNow / waitForTag，未抽公共模块：
 * 两边页面结构完全不同，抽取要连带改 Braidge 线上埋点；本页活动结束即删。没有复制 sendUrgent（lp 版用来等结账跳转），
 * 本页去商详不拦截、不延迟跳转，靠 pagehide 时 flushNow 交出即可（见下方 pagehide 监听处的消融记录）。
 * 口径同 lp：等渠道注入的 gtag 在 dataLayer 里 config 了媒体资源再发，与渠道 page_view / add_to_cart 同一 client、同一会话；
 * 就绪后隔 1s 起发、逐条间隔 300ms；离开页面剩下的立即发出；渠道 30s 都没配置（拒绝 Cookie / 拦截）就一条不发。
 * 调试：URL 带 preview_theme_id 或 debug=1 时加 debug_mode，GA DebugView 可见。
 */
(() => {
  const root = document.querySelector('[data-hw]');
  if (!root) return;

  const MEASUREMENT_ID = 'G-NVYK9MTWK2';
  const DWELL_MS = 1000;
  const TAG_POLL_MS = 500;
  const TAG_WAIT_MS = 30000;
  const TAG_SETTLE_MS = 1000;
  const SEND_GAP_MS = 300;
  const SCROLL_MARKS = [25, 50, 75, 90, 100];
  const THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);
  const SECTIONS = [
    ['hero', '.hw-hero'],
    ['countdown', '.hw-count'],
    ['steps', '.hw-steps'],
    ['spin', '[data-hw-spin]'],
    ['shoes', '[data-hw-shoes]'],
    ['extra', '[data-hw-extra]'],
    ['faq', '.hw-faq']
  ];

  const query = new URLSearchParams(window.location.search);
  const debug = query.has('preview_theme_id') || query.get('debug') === '1';

  /* ------------------------------------------------------------ 发送 */
  // 渠道的 gtag 把自己的 config 放进 dataLayer 后，媒体资源才算就绪
  const tagReady = () => Array.isArray(window.dataLayer)
    && window.dataLayer.some((command) => command?.[0] === 'config' && command?.[1] === MEASUREMENT_ID);

  const outbox = [];
  let transport = 'idle'; // idle → waiting → ready | gone
  let pacing = false;

  // window.gtag 是 layout/theme.liquid 里的官方存根（push arguments 对象）
  const emit = ([name, params]) => window.gtag?.('event', name, {
    ...params,
    send_to: MEASUREMENT_ID,
    ...(debug ? { debug_mode: true } : {})
  });

  const pump = () => {
    if (transport !== 'ready' || pacing || !outbox.length) return;
    emit(outbox.shift());
    pacing = true;
    window.setTimeout(() => {
      pacing = false;
      pump();
    }, SEND_GAP_MS);
  };

  // 离开页面等不了间隔：剩下的一次发出
  const flushNow = () => {
    if (transport === 'gone' || !tagReady()) return;
    transport = 'ready';
    outbox.splice(0).forEach(emit);
  };

  const waitForTag = () => {
    transport = 'waiting';
    const started = Date.now();
    const poll = () => {
      if (tagReady()) {
        // 晚渠道 config 一拍再发，第一条不与它的 page_view 同时到达
        window.setTimeout(() => {
          transport = 'ready';
          pump();
        }, TAG_SETTLE_MS);
        return;
      }
      if (Date.now() - started > TAG_WAIT_MS) {
        transport = 'gone';
        outbox.length = 0;
        return;
      }
      window.setTimeout(poll, TAG_POLL_MS);
    };
    poll();
  };

  // GA4 参数值上限 100 字符；数字、布尔原样
  const clean = (params) => Object.fromEntries(Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key, typeof value === 'string' ? value.slice(0, 100) : value]));

  const send = (name, params) => {
    if (transport === 'gone') return;
    outbox.push([name, clean(params)]);
    if (transport === 'idle') waitForTag();
    pump();
  };

  // 去商详等同一标签页跳走的点击也走普通队列：pagehide 时 flushNow 把队列全部交给 gtag，gtag 在卸载时当场连同攒着的批发出。
  // 2026-10-15 主域 netlog 消融：点击时立即交给 gtag 与否结果相同，去掉这两个 pagehide / hidden 监听则队尾事件丢失
  window.addEventListener('pagehide', flushNow);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flushNow());

  /* ------------------------------------------------------------ 业务脚本派发的结果事件 */
  // 加购带的是面板里格式化好的单价（如 "$129.99"），这里换成 GA 电商口径的 value（单价 × 数量）和 currency
  const priceToValue = ({ price, ...params }) => {
    if (price === undefined) return params;
    const unit = Number(String(price).replace(/[^0-9.]/g, ''));
    return {
      ...params,
      ...(Number.isFinite(unit) && unit > 0 ? { value: Math.round(unit * (params.quantity || 1) * 100) / 100 } : {}),
      currency: window.Shopify?.currency?.active || 'USD'
    };
  };

  document.addEventListener('nx-hw:track', (event) => {
    const { name, params } = event.detail || {};
    if (typeof name !== 'string' || !name.startsWith('hw_')) return;
    send(name, priceToValue(params || {}));
  });

  /* ------------------------------------------------------------ 区块露出 */
  const onScreen = (entry) => entry.isIntersecting && (
    entry.intersectionRatio >= 0.5
    || entry.intersectionRect.height >= (entry.rootBounds?.height || window.innerHeight) * 0.5
  );

  const watches = new Map(SECTIONS.flatMap(([name, selector]) => {
    const node = root.querySelector(selector);
    return node ? [[node, { name, visible: false, timer: 0 }]] : [];
  }));

  // 可见后开始计时，期间一旦移出就作废；比例变化不重置计时，慢慢滚也能在 1s 后记到
  const observer = 'IntersectionObserver' in window && watches.size
    ? new IntersectionObserver((entries) => entries.forEach((entry) => {
      const watch = watches.get(entry.target);
      if (!watch) return;
      watch.visible = onScreen(entry);
      if (!watch.visible) {
        window.clearTimeout(watch.timer);
        watch.timer = 0;
        return;
      }
      if (watch.timer) return;
      watch.timer = window.setTimeout(() => {
        watch.timer = 0;
        if (!watch.visible) return;
        watches.delete(entry.target);
        observer.unobserve(entry.target);
        send('hw_section_view', { section_name: watch.name });
      }, DWELL_MS);
    }), { threshold: THRESHOLDS })
    : null;
  watches.forEach((_, node) => observer?.observe(node));

  /* ------------------------------------------------------------ 浏览深度（算法同 lp / pdp-analytics） */
  const reachedMarks = new Set();
  let scrollQueued = false;

  const checkScroll = () => {
    scrollQueued = false;
    const { body, documentElement: page } = document;
    const docHeight = Math.max(body.scrollHeight, page.scrollHeight, body.offsetHeight, page.offsetHeight);
    const maxScroll = docHeight - (window.innerHeight || page.clientHeight);
    if (maxScroll <= 0) return;
    const percent = Math.min(100, Math.round((window.scrollY / maxScroll) * 100));
    SCROLL_MARKS.filter((mark) => percent >= mark && !reachedMarks.has(mark)).forEach((mark) => {
      reachedMarks.add(mark);
      send('hw_scroll_depth', { percent_scrolled: mark });
    });
  };

  window.addEventListener('scroll', () => {
    if (scrollQueued) return;
    scrollQueued = true;
    window.requestAnimationFrame(checkScroll);
  }, { passive: true });
  window.setTimeout(checkScroll, 500);

  /* ------------------------------------------------------------ 抽奖提交 */
  // 输入框回车是隐式提交，浏览器把表单里第一个提交按钮当 submitter，所以靠 keydown 标记；标记只活到本轮任务结束
  let enterPressed = false;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof Element) || !event.target.matches('[data-hw-email]')) return;
    enterPressed = true;
    window.setTimeout(() => { enterPressed = false; }, 0);
  }, true);

  // 捕获阶段：先于业务脚本的 submit 处理，hw_spin_click 排在它派发的 hw_spin_result 前面
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof Element) || !form.matches('[data-hw-form]')) return;
    let location = 'form';
    if (enterPressed) location = 'keyboard';
    else if (event.submitter?.closest('[data-hw-wheel]')) location = 'hub';
    send('hw_spin_click', { button_location: location });
  }, true);

  /* ------------------------------------------------------------ 页面内点击 */
  const handleOf = (card) => card?.querySelector('a[href*="/products/"]')?.getAttribute('href').match(/\/products\/([^/?#]+)/)?.[1] || '';

  // 捕获阶段：要在业务脚本改状态（展开 View more、开合 i 提示）之前读到点击那一刻的状态
  document.addEventListener('click', ({ target }) => {
    if (!(target instanceof Element) || !root.contains(target)) return;

    const card = target.closest('[data-hwp]');
    if (card) {
      if (target.closest('[data-hwp-open]')) return send('hw_product_click', { action: 'open_sheet', product_handle: handleOf(card) });
      if (target.closest('[data-hwp-sizechart]')) return send('hw_product_click', { action: 'size_chart', product_handle: handleOf(card) });
      // 卡面图片和名称都是同一标签页去商详；面板里没有商品链接
      if (target.closest('a[href*="/products/"]') && !target.closest('[data-hwp-sheet]')) {
        return send('hw_product_click', { action: 'view_product', product_handle: handleOf(card) });
      }
      return;
    }

    if (target.closest('[data-hw-scroll]')?.closest('.hw-hero')) return send('hw_cta_click', { cta_name: 'hero_spin' });
    if (target.closest('[data-hw-shop]')?.closest('[data-hw-sticky]')) return send('hw_cta_click', { cta_name: 'sticky_shop' });
    // 同一个按钮展开 / 收起，只记展开
    const more = target.closest('[data-hw-more]');
    if (more) return more.getAttribute('aria-expanded') !== 'true' && send('hw_cta_click', { cta_name: 'view_more' });

    const app = target.closest('[data-hw-share-app]');
    if (app) return send('hw_share', { action: 'app', app: app.dataset.hwShareApp });

    const account = target.closest('[data-hw-ugc]');
    if (account) return send('hw_ugc_click', { action: 'account', channel: account.dataset.hwUgc });
    // 同一个 i 图标点开 / 点收，只记点开（网页端悬停就能看到，不经过点击）
    const tip = target.closest('[data-hw-tip-btn]');
    if (tip) tip.getAttribute('aria-expanded') !== 'true' && send('hw_ugc_click', { action: 'info' });
  }, true);
})();
